import { useState, useRef, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SARAH_IMG = 'https://images.pexels.com/photos/6535381/pexels-photo-6535381.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop&crop=face';

const GREETINGS = {
  en: { hello: "Hello", morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening" },
  es: { hello: "Hola", morning: "Buenos dias", afternoon: "Buenas tardes", evening: "Buenas noches" },
  fr: { hello: "Bonjour", morning: "Bonjour", afternoon: "Bon apres-midi", evening: "Bonsoir" },
  de: { hello: "Hallo", morning: "Guten Morgen", afternoon: "Guten Tag", evening: "Guten Abend" },
  it: { hello: "Ciao", morning: "Buongiorno", afternoon: "Buon pomeriggio", evening: "Buonasera" },
  pt: { hello: "Ola", morning: "Bom dia", afternoon: "Boa tarde", evening: "Boa noite" },
  ja: { hello: "こんにちは", morning: "おはようございます", afternoon: "こんにちは", evening: "こんばんは" },
  ko: { hello: "안녕하세요", morning: "좋은 아침", afternoon: "안녕하세요", evening: "안녕하세요" },
  zh: { hello: "你好", morning: "早上好", afternoon: "下午好", evening: "晚上好" },
  ar: { hello: "مرحبا", morning: "صباح الخير", afternoon: "مساء الخير", evening: "مساء الخير" },
  nl: { hello: "Hallo", morning: "Goedemorgen", afternoon: "Goedemiddag", evening: "Goedenavond" },
  pl: { hello: "Cześć", morning: "Dzień dobry", afternoon: "Dzień dobry", evening: "Dobry wieczór" },
  ru: { hello: "Привет", morning: "Доброе утро", afternoon: "Добрый день", evening: "Добрый вечер" },
  hi: { hello: "नमस्ते", morning: "सुप्रभात", afternoon: "नमस्ते", evening: "शुभ संध्या" },
  tl: { hello: "Kumusta", morning: "Magandang umaga", afternoon: "Magandang hapon", evening: "Magandang gabi" },
};

function getTimeGreeting(langCode) {
  const hour = new Date().getHours();
  const lang = GREETINGS[langCode] || GREETINGS.en;
  if (hour < 12) return lang.morning;
  if (hour < 18) return lang.afternoon;
  return lang.evening;
}

function detectLanguage() {
  const nav = navigator.language || navigator.userLanguage || 'en';
  const code = nav.split('-')[0].toLowerCase();
  return GREETINGS[code] ? code : 'en';
}

export default function SarahChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `tfg_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const chatRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      const lang = detectLanguage();
      const timeGreeting = getTimeGreeting(lang);
      const isEnglish = lang === 'en';

      let greeting;
      if (isEnglish) {
        greeting = `${timeGreeting}! I'm Sarah, your personal size guide. Need help finding your perfect fit? Is English OK, or do you prefer another language?`;
      } else {
        const englishGreeting = getTimeGreeting('en');
        greeting = `${timeGreeting}! / ${englishGreeting}! I'm Sarah, your personal size guide. I noticed you might prefer ${lang.toUpperCase()}. Feel free to write in any language — I'll respond in yours! Need help finding your perfect fit?`;
      }
      setMessages([{ role: 'sarah', text: greeting }]);
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/avatar/chat`, { message: userMsg, session_id: sessionId });
      setMessages(prev => [...prev, { role: 'sarah', text: r.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'sarah', text: "Sorry, I'm having trouble right now. Try our size finder tools!" }]);
    }
    setLoading(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-20 right-4 z-40 w-14 h-14 bg-[#1C1917] text-white rounded-full shadow-xl flex items-center justify-center hover:bg-[#C5A028] transition-all hover:scale-105 animate-sarah-pulse" aria-label="Chat with Sarah" data-testid="sarah-toggle">
          <img src={SARAH_IMG} alt="Sarah" className="w-10 h-10 rounded-full object-cover" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-4 right-4 z-50 w-80 sm:w-96 bg-white border border-[#E8E2D9] shadow-2xl flex flex-col" style={{ height: '28rem' }} data-testid="sarah-chat-panel">
          <div className="flex items-center gap-3 p-4 border-b border-[#E8E2D9] bg-[#FAF8F4]">
            <img src={SARAH_IMG} alt="Sarah" className="w-9 h-9 rounded-full object-cover" />
            <div className="flex-1">
              <p className="font-semibold text-sm text-[#1C1917]">Sarah</p>
              <p className="text-xs text-[#78716C]">AI Size Guide - Speaks Your Language</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#E8E2D9] rounded transition-colors" aria-label="Close chat" data-testid="sarah-close">
              <X className="w-4 h-4 text-[#78716C]" />
            </button>
          </div>

          <div ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 text-sm ${m.role === 'user' ? 'bg-[#1C1917] text-white' : 'bg-[#FAF8F4] text-[#1C1917] border border-[#E8E2D9]'}`}>
                  <p>{m.text}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-3 text-sm text-[#78716C]">
                  <span className="animate-pulse">Sarah is typing...</span>
                </div>
              </div>
            )}
          </div>

          <p className="px-4 py-1 text-[10px] text-[#78716C] bg-[#FAF8F4] border-t border-[#E8E2D9]">Sarah is an AI assistant. For specific fit, contact the retailer.</p>

          <div className="p-3 border-t border-[#E8E2D9] flex gap-2">
            <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="Ask about sizing in any language..." className="flex-1 p-2 text-sm border border-[#E8E2D9] focus:border-[#C5A028] focus:outline-none" aria-label="Chat message" data-testid="sarah-input" />
            <button onClick={send} disabled={loading || !input.trim()} className="px-3 bg-[#1C1917] text-white hover:bg-[#C5A028] transition-colors disabled:opacity-50" aria-label="Send message" data-testid="sarah-send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
